import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import DragHandle from './DragHandle.vue';
import {
  create_main_section,
  delete_main_section,
  fetch_all_main_sections,
  fetch_all_sections_catalog,
  reorder_main_sections,
  reorder_form_section,
  create_sub_section as create_sub_section_request,
  fetch_main_sections_by_form,
  soft_delete_form_section,
  update_main_section,
} from '@/services/main_section_service';
import {
  assign_dependent_question,
  create_question,
  fetch_question_for_edit,
  fetch_questions_in_project,
  reorder_section_question,
  unassign_question_from_section,
  update_question,
} from '@/services/question_service';
import DependentQuestionsPanel from '@/components/config_components/DependentQuestions/DependentQuestionsPanel.vue';
import {
  ASSIGN_QUESTIONS_TO_SECTION_URL,
  GET_VERIFICATION_SERVICES,
  UPDATE_FORM_SECTION_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  extract_error_message,
  parse_response_data as parse_api_response_data,
} from '@/utils/api_response_helpers';

export default {
  name: 'CreateSections',
  mixins: [alert_mixin],
  components: {
    DragHandle,
    DependentQuestionsPanel,
  },
    data: () => ({
    creating_main_section: false,
    editing_main_section: false,
      deleting_main_section: false,
      deleting_sub_section: false,
    reordering_main_sections: false,
      reordering_sub_sections: false,
      reordering_section_questions: false,
    creating_question: false,
    editing_question: false,
      dragged_main_section: null,
      dragged_sub_section: null,
      dragged_question: null,
    main_section_modal_open: false,
    subsection_modal_open: false,
    question_modal_open: false,
      assign_existing_question_modal_open: false,
    edit_question_modal_open: false,
    order_number_error: false,
    main_section_name_error: false,
    sub_section_name_error: false,
    edit_main_section_name_error: false,
    edit_section_name_error: false,
    question_text_error: false,
    edit_question_error: false,
    active_question_section: null,
      active_assign_section: null,
    active_question_section_index: null,
    active_question_index: null,
    active_edit_section_index: null,
    current_edit_section_index: null,
    active_main_section_name: '',
    active_main_section_id: null,
    main_sections: [],
    section_id_counter: 0,
    question_id_counter: 0,
    topBar: {
      label: 'Editing :',
      formName: '',
      formId: '',
    },
    hero: {
      title: 'Form Structure',
      description: 'Organize your form into logical sections.',
    },
    sections: [],
    new_main_section: {
      main_section_name: '',
      order_number: 0,
      existing_main_section_id: '',
    },
    new_sub_section: {
      section_name: '',
      order_number: 0,
      existing_section_id: '',
    },
    main_section_mode: 'new',
    sub_section_mode: 'new',
    reusable_main_sections: [],
    reusable_sub_sections: [],
    current_edit_section: {
      section_id: null,
      name: '',
      is_deleting: false,
    },
    current_edit_main_section: {
      main_section_id: null,
      main_section_name: '',
      main_section_description: '',
      is_deleting: false,
    },
    question_forms: [],
    current_edit_question: {
      question_id: null,
      question_text: '',
      question_description: '',
      question_type_id: '',
      is_mandatory: true,
      is_unique: false,
      question_number: '',
      show_option_inputs: false,
      option_inputs: ['', ''],
      multi_select_options: [],
      dependant_questions: [],
    },
      is_loading_questions: false,
      question_search: '',
      question_list: [],
    actions: {
      add_section_label: 'Add Main Section',
      add_sub_section_label: 'Add Sub Section',
      add_question_label: 'Add Question',
        assign_existing_question_label: 'Assign Existing Question',
      edit_question_label: 'Edit Question',
    },
    sidebar: {
      preview_title: 'Preview Live Form',
      preview_description: 'See how it looks for suppliers',
    },
    verification_guidance_loading: false,
    verification_form_id: '',
    verification_services: [],
    verification_active_service_id: '',
    verification_required_fields_by_service: {},
  }),
  computed: {
    is_verification_form_context() {
      return Boolean(String(this.verification_form_id || '').trim());
    },
    verification_active_service() {
      const id = String(this.verification_active_service_id || '').trim();
      if (!id) return null;
      return (this.verification_services || []).find((svc) => String(svc?.id) === id) || null;
    },

    verification_active_required_fields() {
      const id = String(this.verification_active_service_id || '').trim();
      const required = Reflect.get(this.verification_required_fields_by_service ?? {}, id) || [];
      return Array.isArray(required) ? required : [];
    },
    question_types() {
      return (this.$store.getters.questionTypes || []).map((type) => ({
        ...type,
        formatted_type: this.format_question_type_label(type.question_type || type.formatted_type || ''),
      }));
    },
    question_types_loading() {
      return this.$store.getters.formMetadataLoading;
    },
    section_groups() {
      return (Array.isArray(this.main_sections) ? this.main_sections : [])
        .map((main_section, index) => ({
          ...main_section,
          form_main_section_id: Number(
            main_section.form_main_section_id ?? main_section.backend_id ?? main_section.id ?? null
          ) || null,
          main_section_id: Number(main_section.main_section_id ?? null) || null,
          main_section_name:
            String(main_section.main_section_name || main_section.name || '').trim() ||
            'Untitled Main Section',
          order_number: Number(main_section.order_number ?? index + 1),
          sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) => ({
            ...section,
            section_index: this.sections.findIndex(
              (flat_section) =>
                Number(flat_section.id ?? flat_section.backend_id ?? null) ===
                Number(section.id ?? section.backend_id ?? null)
            ),
          })),
        }))
        .sort((left, right) => Number(left.order_number) - Number(right.order_number));
    },
    reusable_main_section_options() {
      const assigned_main_section_ids = new Set(
        (Array.isArray(this.main_sections) ? this.main_sections : [])
          .map((item) => Number(item?.main_section_id || 0))
          .filter(Boolean)
      );

      return (Array.isArray(this.reusable_main_sections) ? this.reusable_main_sections : [])
        .filter((item) => !assigned_main_section_ids.has(Number(item?.id || 0)));
    },
    reusable_sub_section_options() {
      const assigned_section_ids = new Set(
        (Array.isArray(this.sections) ? this.sections : [])
          .map((item) => Number(item?.section_id ?? item?.backend_id ?? item?.id ?? 0))
          .filter(Boolean)
      );

      return (Array.isArray(this.reusable_sub_sections) ? this.reusable_sub_sections : [])
        .filter((item) => !assigned_section_ids.has(Number(item?.id || 0)));
    },
    selected_reusable_main_section() {
      return this.reusable_main_section_options.find(
        (item) => String(item?.id || '') === String(this.new_main_section.existing_main_section_id || '')
      ) || null;
    },
    selected_reusable_sub_section() {
      return this.reusable_sub_section_options.find(
        (item) => String(item?.id || '') === String(this.new_sub_section.existing_section_id || '')
      ) || null;
    },
    active_assign_section_label() {
      const section = this.active_assign_section || {};
      return String(
        section.name ??
        section.section_name ??
        section.section?.name ??
        section.section?.section_name ??
        ''
      ).trim() || 'Selected section';
    },
    active_assign_main_section_label() {
      const section = this.active_assign_section || {};
      return String(
        section.main_section_name ??
        section.form_main_section_name ??
        section.main_section?.main_section_name ??
        section.main_section?.name ??
        ''
      ).trim();
    },
    filtered_questions() {
      const query = String(this.question_search || '').trim().toLowerCase();

      return (Array.isArray(this.question_list) ? this.question_list : []).filter((question) => {
        if (!query) return true;

        return (
          String(question.question_text || '').toLowerCase().includes(query) ||
          String(question.question_type_label || '').toLowerCase().includes(query) ||
          String(question.question_number || '').toLowerCase().includes(query) ||
          String(question.assignment_label || '').toLowerCase().includes(query)
        );
      });
    },
  },
  async created() {
    const form_id = this.$route?.query?.form_id;
    const form_name = this.$route?.query?.form_name;
    const verification_form_id = this.$route?.query?.verification_form_id;
    if (form_id) this.topBar.formId = String(form_id);
    if (form_name) this.topBar.formName = String(form_name);
    if (verification_form_id) this.verification_form_id = String(verification_form_id);

    await Promise.all([
      this.$store.dispatch('ensureFormMetadataLoaded'),
      this.load_main_sections(),
      this.load_form_sections(),
      this.load_reusable_section_catalogs(),
    ]).catch((error) => console.warn('Failed to load form sections', error));

    await this.load_verification_guidance();
  },
  methods: {
    parse_response_data(response) {
      return parse_api_response_data(response);
    },

    format_required_field_label(value) {
      const text = String(value ?? '')
        .replace(/[_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text;
    },

    set_active_verification_service(service_id) {
      const id = String(service_id ?? '').trim();
      if (id) this.verification_active_service_id = id;
    },

    async load_verification_guidance() {
      if (!this.is_verification_form_context) return;
      const verification_form_id = Number(this.verification_form_id) || null;
      if (!verification_form_id) return;

      this.verification_guidance_loading = true;

      try {
        const services_resp = await axios.get(GET_VERIFICATION_SERVICES, get_request_config());
        const services_data = this.parse_response_data(services_resp);
        const services = Array.isArray(services_data?.data) ? services_data.data : [];
        this.verification_services = services;

        const required_by_service = {};

        services.forEach((service) => {
          const id = String(service?.id);
          const required_fields = Array.isArray(service?.required_fields) ? service.required_fields : [];
          Reflect.set(required_by_service, id, required_fields);        });

        this.verification_required_fields_by_service = required_by_service;

        if (!this.verification_active_service_id && services.length) {
          this.verification_active_service_id = String(services[0].id);
        }
      } catch (error) {
        this.verification_services = [];
        this.verification_required_fields_by_service = {};
        console.warn('Failed to load verification guidance', error);
      } finally {
        this.verification_guidance_loading = false;
      }
    },

    normalize_error_message(message, fallback = 'Something went wrong.') {
      if (Array.isArray(message)) {
        return message.map((item) => String(item || '').trim()).filter(Boolean).join(', ') || fallback;
      }

      if (typeof message !== 'string') {
        return fallback;
      }

      const trimmed_message = message.trim();

      if (!trimmed_message) {
        return fallback;
      }

      try {
        const parsed_message = JSON.parse(trimmed_message.replace(/'/g, '"'));

        if (Array.isArray(parsed_message)) {
          return parsed_message.map((item) => String(item || '').trim()).filter(Boolean).join(', ') || fallback;
        }
      } catch {
        return trimmed_message;
      }

      return trimmed_message;
    },

    format_question_type_label(value) {
      return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .trim();
    },

    is_dependent_question(question = {}) {
      return Boolean(
        question?.is_assigned_as_dependent ||
        question?.is_dependent ||
        question?.dependent_question_id ||
        question?.dependent_question?.id ||
        question?.main_question_id ||
        question?.parent_question_id ||
        question?.parent_question?.id ||
        question?.trigger_condition
      );
    },

    build_question_form(order_number = 1) {
      const result = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        question_text: '',
        question_description: '',
        question_type_id: '',
        is_mandatory: true,
        is_unique: false,
        question_number: order_number,
        multi_select_options: [],
        trigger_condition: null,
        dependant_questions: [],
        show_option_inputs: false,
        option_inputs: ['', ''],
      };

      return result;
    },

    normalize_question(question, index = 0) {
      if (question && typeof question === 'object') {
        const question_type_value =
          question.question_type?.id ??
          question.question_type_id ??
          question.question_type?.question_type ??
          question.question_type?.type ??
          question.type ??
          '';
        const option_inputs = Array.isArray(question?.multi_select_choices)
          ? question.multi_select_choices.map((choice) =>
              choice && typeof choice === 'object'
                ? String(choice.choice ?? choice.value ?? choice.option ?? choice.name ?? '')
                : String(choice || '')
            )
          : Array.isArray(question?.options)
            ? question.options.map((choice) =>
                choice && typeof choice === 'object'
                  ? String(choice.choice ?? choice.value ?? choice.option ?? choice.name ?? '')
                  : String(choice || '')
              )
            : [];

        const question_id =
          question.question_id ??
          question.id ??
          question.question?.id ??
          question.backend_id ??
          null;

        return {
          id: question_id ?? `${Date.now()}-${index}`,
          backend_id: question_id ?? null,
          question_id,
          section_question_id:
            question.section_question_id ??
            question.section_question?.id ??
            question.form_section_question_id ??
            question.section_question_mapping_id ??
            null,
          text:
            question.question_text ??
            question.question ??
            question.text ??
            question.name ??
            `Question ${index + 1}`,
          description: question.question_description ?? '',
          question_number: question.question_number ?? '',
          question_type_id: question_type_value,
          is_mandatory: Boolean(question.is_mandatory ?? true),
          is_unique: Boolean(question.is_unique),
          show_option_inputs:
            String(question_type_value || '')
              .toLowerCase()
              .includes('single_select') ||
            String(question_type_value || '')
              .toLowerCase()
              .includes('multi_select'),
          option_inputs: option_inputs.length >= 2 ? option_inputs : ['', ''],
          dependant_questions: Array.isArray(question.dependent_questions)
            ? question.dependent_questions
            : Array.isArray(question.dependant_questions)
              ? question.dependant_questions
              : [],
          is_unassigning: false,
          type_label: this.format_question_type_label(
            question.question_type?.question_type ??
            question.question_type?.type ??
            question.question_type ??
            question.type ??
            ''
          ),
        };
      }

      return {
        id: `${Date.now()}-${index}`,
        text: String(question ?? `Question ${index + 1}`),
        description: '',
        type_label: '',
      };
    },

    normalize_section(item, index = 0) {
      const section = item?.section ?? item ?? {};
      const raw_questions = Array.isArray(item?.questions) ? item.questions : [];
      const main_section_id =
        item?.main_section_id ??
        item?.form_main_section_id ??
        item?.section_main_section_id ??
        section?.main_section_id ??
        section?.form_main_section_id ??
        section?.section_main_section_id ??
        section?.main_section?.id ??
        section?.main_section?.main_section_id ??
        null;
      const section_id =
        item?.section_id ??
        item?.id ??
        section?.section_id ??
        section?.id ??
        null;

      return {
        id: section_id ?? `${Date.now()}-${index}`,
        backend_id: section_id ?? null,
        section_id: section_id ?? null,
        main_section_id,
        order_number: Number(item?.order_number ?? index + 1),
        name:
          item?.name ??
          item?.section_name ??
          section?.name ??
          section?.section_name ??
          'Untitled Section',
        main_section_name:
          item?.main_section_name ??
          section?.main_section_name ??
          item?.form_main_section_name ??
          section?.form_main_section_name ??
          section?.main_section?.main_section_name ??
          '',
        description: section?.description ?? '',
        expanded: index === 0,
        is_deleting: false,
        questions: raw_questions.map((question, question_index) =>
          this.normalize_question(question, question_index)
        ),
      };
    },

    normalize_main_section(item, index = 0) {
      const main_section = item?.main_section ?? item ?? {};
      const raw_sections = Array.isArray(item?.sub_sections)
        ? item.sub_sections
        : Array.isArray(main_section?.sub_sections)
          ? main_section.sub_sections
          : [];

      return {
        id: item?.main_section_id ?? main_section?.id ?? item?.id ?? `${Date.now()}-${index}`,
        backend_id: item?.id ?? main_section?.id ?? null,
        form_main_section_id: item?.id ?? main_section?.id ?? null,
        main_section_id: item?.main_section_id ?? main_section?.id ?? item?.id ?? null,
        order_number: Number(item?.order_number ?? main_section?.order_number ?? index + 1),
        name:
          item?.name ??
          item?.main_section_name ??
          main_section?.name ??
          main_section?.main_section_name ??
          'Untitled Main Section',
        description:
          item?.main_section_description ??
          item?.description ??
          main_section?.main_section_description ??
          main_section?.description ??
          '',
        sections: raw_sections.map((sub_section, sub_index) =>
          this.normalize_section(
            {
              ...sub_section,
              section_id: sub_section?.section_id ?? sub_section?.id ?? sub_section?.section?.id ?? null,
              name: sub_section?.name ?? sub_section?.section_name ?? sub_section?.section?.name ?? '',
              form_main_section_id: item?.id ?? main_section?.id ?? null,
              main_section_id: item?.main_section_id ?? main_section?.id ?? item?.id ?? null,
              main_section_name:
                item?.main_section_name ??
                main_section?.main_section_name ??
                main_section?.name ??
                '',
            },
            sub_index
          )
        ),
      };
    },

    async load_main_sections() {
      const formId = Number(this.topBar.formId);

      if (!formId) {
        this.main_sections = [];
        this.sections = [];
        return;
      }

      const items = await fetch_main_sections_by_form(formId);
      this.main_sections = items
        .map((item, index) => this.normalize_main_section(item, index))
        .sort((left, right) => Number(left.order_number) - Number(right.order_number));
      let flat_index = 0;
      this.sections = [];
      this.main_sections.forEach((main_section) => {
        (Array.isArray(main_section.sections) ? main_section.sections : []).forEach((section) => {
          this.sections.push({
            ...section,
            original_index: flat_index,
          });
          flat_index += 1;
        });
      });
    },

    async load_form_sections() {
      return this.load_main_sections();
    },

    async load_reusable_section_catalogs() {
      const [main_sections, sections] = await Promise.all([
        fetch_all_main_sections().catch(() => []),
        fetch_all_sections_catalog().catch(() => []),
      ]);

      this.reusable_main_sections = Array.isArray(main_sections) ? main_sections : [];
      this.reusable_sub_sections = Array.isArray(sections) ? sections : [];
    },

    open_main_section_modal() {
      this.main_section_name_error = false;
      this.order_number_error = false;
      this.main_section_mode = 'new';
      this.new_main_section = {
        main_section_name: '',
        order_number: this.main_sections.length + 1,
        existing_main_section_id: '',
      };
      this.main_section_modal_open = true;
    },

    open_sub_section_modal(main_section_id = null) {
      this.sub_section_name_error = false;
      this.order_number_error = false;
      this.sub_section_mode = 'new';
      const fallback_group = this.main_sections?.[0] || this.section_groups?.[0] || {};
      const resolved_main_section_id =
        main_section_id
        ?? fallback_group.form_main_section_id
        ?? fallback_group.backend_id
        ?? '';
      this.active_main_section_id = resolved_main_section_id === '' || resolved_main_section_id == null
        ? ''
        : String(resolved_main_section_id);
      this.active_main_section_name =
        this.active_main_section_id === ''
          ? ''
          : String(
              this.section_groups.find(
                (group) => String(group.form_main_section_id) === String(this.active_main_section_id)
              )
                ?.main_section_name ||
                fallback_group.main_section_name ||
                fallback_group.name ||
                ''
            ).trim();
      const active_main_section = this.section_groups.find(
        (group) => String(group.form_main_section_id) === String(this.active_main_section_id)
      );
      const next_sub_section_order = Array.isArray(active_main_section?.sections)
        ? active_main_section.sections.length + 1
        : 1;
      this.new_sub_section = {
        section_name: '',
        order_number: next_sub_section_order,
        existing_section_id: '',
      };
      this.subsection_modal_open = true;
    },

    resolve_main_section_name() {
      if (this.main_section_mode === 'reuse') {
        return String(this.selected_reusable_main_section?.name || '').trim();
      }

      return String(this.new_main_section.main_section_name || '').trim();
    },

    resolve_sub_section_name() {
      if (this.sub_section_mode === 'reuse') {
        return String(this.selected_reusable_sub_section?.name || '').trim();
      }

      return String(this.new_sub_section.section_name || '').trim();
    },

    open_edit_section_modal(section_index) {
      const section =
        section_index && typeof section_index === 'object'
          ? section_index
          : this.sections[Number(section_index)];
      const section_id = Number(section?.backend_id ?? section?.id);

      if (!section || !section_id) {
        this.show_error('Please select a valid sub section first.');
        return;
      }

      this.edit_section_name_error = false;
      this.current_edit_section_index = section_index;
      this.current_edit_section = {
        section_id,
        name: section.name || '',
        is_deleting: Boolean(section?.is_deleting),
      };
    },

    open_edit_main_section_modal(main_section) {
      const main_section_id = Number(
        main_section?.main_section_id ?? main_section?.id ?? main_section?.backend_id ?? null
      );

      if (!main_section || !main_section_id) {
        this.show_error('Please select a valid main section first.');
        return;
      }

      this.edit_main_section_name_error = false;
      this.current_edit_main_section = {
        main_section_id,
        main_section_name: String(
          main_section?.main_section_name ?? main_section?.name ?? ''
        ).trim(),
        main_section_description: String(main_section?.description ?? '').trim(),
        is_deleting: Boolean(main_section?.is_deleting),
      };
    },

    handle_drag_over(event) {
      event.preventDefault();

      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    },

    handle_main_section_drag_start(event, main_section, main_section_index) {
      if (this.reordering_main_sections) {
        return;
      }

      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(
          'text/plain',
          String(main_section?.main_section_id ?? main_section?.id ?? main_section_index)
        );
      }

      this.dragged_main_section = {
        main_section_id: Number(
          main_section?.main_section_id ?? main_section?.id ?? main_section?.backend_id ?? null
        ),
        main_section_index,
      };
    },

    handle_main_section_drag_end() {
      this.dragged_main_section = null;
    },

    async handle_main_section_drop(target_main_section, target_index) {
      if (!this.dragged_main_section || this.reordering_main_sections) {
        return;
      }

      const target_main_section_id = Number(
        target_main_section?.main_section_id ??
        target_main_section?.id ??
        target_main_section?.backend_id ??
        null
      );

      if (
        !target_main_section_id ||
        Number(this.dragged_main_section.main_section_id) === target_main_section_id
      ) {
        this.dragged_main_section = null;
        return;
      }

      await this.reorder_main_sections_by_index(this.dragged_main_section.main_section_index, target_index);
      this.dragged_main_section = null;
    },

    async reorder_main_sections_by_index(source_index, target_index) {
      if (this.reordering_main_sections) {
        return;
      }

      const formId = Number(this.topBar.formId);

      if (!formId) {
        this.show_error('Please select a valid form section first.');
        return;
      }

      const ordered_sections = [...this.section_groups];
      if (
        source_index < 0 ||
        target_index < 0 ||
        source_index >= ordered_sections.length ||
        target_index >= ordered_sections.length
      ) {
        return;
      }

      if (source_index === target_index) {
        return;
      }

      const reordered_sections = [...ordered_sections];
      const [moved_main_section] = reordered_sections.splice(source_index, 1);

      if (!moved_main_section) {
        return;
      }

      reordered_sections.splice(target_index, 0, moved_main_section);

      const payload = {
        form_id: formId,
        reordered_main_sections: reordered_sections.map((item, index) => ({
          main_section_id: Number(item.main_section_id ?? item.id ?? item.backend_id ?? null),
          order_number: index + 1,
        })),
      };

      this.reordering_main_sections = true;
      const loader = await this.show_loader('Reordering main sections...');

      try {
        const parsed_response = await reorder_main_sections(payload);

        if (parsed_response?.status === 'success') {
          await this.load_main_sections();
          this.show_success(parsed_response.message || 'Main sections reordered successfully.');
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to reorder main sections.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to reorder main sections.'));
      } finally {
        await this.close_loader(loader);
        this.reordering_main_sections = false;
      }
    },

    handle_sub_section_drag_start(event, main_section, sub_section, sub_section_index) {
      if (this.reordering_sub_sections) {
        return;
      }

      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(
          'text/plain',
          String(sub_section?.section_id ?? sub_section?.id ?? sub_section_index)
        );
      }

      this.dragged_sub_section = {
        main_section_id: Number(
          main_section?.main_section_id ?? main_section?.id ?? main_section?.backend_id ?? null
        ),
        section_id: Number(sub_section?.section_id ?? sub_section?.backend_id ?? sub_section?.id ?? null),
        sub_section_index,
      };
    },

    handle_sub_section_drag_end() {
      this.dragged_sub_section = null;
    },

    async handle_sub_section_drop(main_section, target_sub_section, target_index) {
      if (!this.dragged_sub_section || this.reordering_sub_sections) {
        return;
      }

      const current_main_section_id = Number(
        main_section?.main_section_id ?? main_section?.id ?? main_section?.backend_id ?? null
      );
      const target_section_id = Number(
        target_sub_section?.section_id ?? target_sub_section?.backend_id ?? target_sub_section?.id ?? null
      );

      if (
        !current_main_section_id ||
        Number(this.dragged_sub_section.main_section_id) !== current_main_section_id ||
        !target_section_id ||
        Number(this.dragged_sub_section.section_id) === target_section_id
      ) {
        this.dragged_sub_section = null;
        return;
      }

      await this.reorder_sub_sections_by_index(
        main_section,
        this.dragged_sub_section.sub_section_index,
        target_index
      );
      this.dragged_sub_section = null;
    },

    async reorder_sub_sections_by_index(main_section, source_index, target_index) {
      if (this.reordering_sub_sections) {
        return;
      }

      const form_main_section_id = Number(
        main_section?.main_section_id ??
        main_section?.id ??
        main_section?.backend_id ??
        null
      );

      if (!form_main_section_id) {
        this.show_error('Please select a valid sub section first.');
        return;
      }

      const ordered_sections = Array.isArray(main_section?.sections)
        ? [...main_section.sections]
        : [];

      if (
        source_index < 0 ||
        target_index < 0 ||
        source_index >= ordered_sections.length ||
        target_index >= ordered_sections.length
      ) {
        return;
      }

      if (source_index === target_index) {
        return;
      }

      const reordered_sections = [...ordered_sections];
      const [moved_section] = reordered_sections.splice(source_index, 1);

      if (!moved_section) {
        return;
      }

      reordered_sections.splice(target_index, 0, moved_section);

      const form_section_id = Number(
        moved_section?.section_id ?? moved_section?.backend_id ?? moved_section?.id ?? null
      );

      if (!form_section_id || !form_main_section_id) {
        this.show_error('Please select a valid sub section first.');
        return;
      }

      const payload = {
        form_section_id,
        form_main_section_id,
        order_number: target_index + 1,
      };

      this.reordering_sub_sections = true;
      const loader = await this.show_loader('Reordering sub sections...');

      try {
        const parsed_response = await reorder_form_section(payload);

        if (parsed_response?.status === 'success') {
          await this.load_main_sections();
          this.expand_main_section_by_id(form_main_section_id);
          this.show_success(parsed_response.message || 'Sections reordered successfully.');
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to reorder sections.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to reorder sections.'));
      } finally {
        await this.close_loader(loader);
        this.reordering_sub_sections = false;
      }
    },

    handle_question_drag_start(event, section, question, question_index) {
      if (this.reordering_section_questions) {
        return;
      }

      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(
          'text/plain',
          String(question?.question_id ?? question?.backend_id ?? question?.id ?? question_index)
        );
      }

      this.dragged_question = {
        section_id: Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null),
        question_id: Number(question?.question_id ?? question?.backend_id ?? question?.id ?? null),
        question_index,
      };
    },

    handle_question_drag_end() {
      this.dragged_question = null;
    },

    async handle_question_drop(section, target_question, target_index) {
      if (!this.dragged_question || this.reordering_section_questions) {
        return;
      }

      const source_section_id = Number(this.dragged_question.section_id);
      const target_section_id = Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null);
      const target_question_id = Number(
        target_question?.question_id ?? target_question?.backend_id ?? target_question?.id ?? null
      );

      if (!source_section_id || source_section_id !== target_section_id || !target_question_id) {
        this.dragged_question = null;
        return;
      }

      if (this.dragged_question.question_index === target_index) {
        this.dragged_question = null;
        return;
      }

      await this.reorder_section_questions(section, this.dragged_question.question_index, target_index);
      this.dragged_question = null;
    },

    async reorder_section_questions(section, source_index, target_index) {
      const ordered_questions = Array.isArray(section?.questions) ? [...section.questions] : [];

      if (
        source_index < 0 ||
        target_index < 0 ||
        source_index >= ordered_questions.length ||
        target_index >= ordered_questions.length
      ) {
        return;
      }

      const reordered_questions = [...ordered_questions];
      const [moved_question] = reordered_questions.splice(source_index, 1);

      if (!moved_question) {
        return;
      }

      reordered_questions.splice(target_index, 0, moved_question);

      const section_question_id = Number(
        moved_question?.section_question_id ??
        moved_question?.question_id ??
        moved_question?.backend_id ??
        moved_question?.id ??
        null
      );

      if (!section_question_id) {
        this.show_error('Could not find the selected question mapping.');
        return;
      }

      const payload = {
        section_question_id,
        order_number: target_index + 1,
      };

      this.reordering_section_questions = true;
      const loader = await this.show_loader('Reordering questions...');

      try {
        const parsed_response = await reorder_section_question(payload);

        if (parsed_response?.status === 'success') {
          await this.load_main_sections();
          this.expand_section_by_id(section?.section_id ?? section?.backend_id ?? section?.id ?? null);

          this.show_success(parsed_response.message || 'Questions reordered successfully.');
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to reorder questions.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to reorder questions.'));
      } finally {
        await this.close_loader(loader);
        this.reordering_section_questions = false;
      }
    },

    expand_section_by_id(section_id) {
      const target_section_id = Number(section_id);

      if (!target_section_id) {
        return;
      }

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => ({
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) =>
          Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === target_section_id
            ? {
                ...section,
                expanded: true,
              }
            : section
        ),
      }));

      this.sections = (Array.isArray(this.sections) ? this.sections : []).map((section) =>
        Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === target_section_id
          ? {
              ...section,
              expanded: true,
            }
          : section
        );
    },

    set_question_unassigning(section_id, question_id, is_unassigning) {
      const normalized_section_id = Number(section_id);
      const normalized_question_id = Number(question_id);

      if (!normalized_section_id || !normalized_question_id) {
        return;
      }

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => ({
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) =>
          Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === normalized_section_id
            ? {
              ...section,
              questions: (Array.isArray(section.questions) ? section.questions : []).map((question) =>
                Number(question?.question_id ?? question?.backend_id ?? question?.id ?? null) === normalized_question_id
                  ? {
                    ...question,
                    is_unassigning,
                  }
                  : question
              ),
            }
            : section
        ),
      }));

      this.sections = (Array.isArray(this.sections) ? this.sections : []).map((section) =>
        Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === normalized_section_id
          ? {
            ...section,
            questions: (Array.isArray(section.questions) ? section.questions : []).map((question) =>
              Number(question?.question_id ?? question?.backend_id ?? question?.id ?? null) === normalized_question_id
                ? {
                  ...question,
                  is_unassigning,
                }
                : question
            ),
          }
          : section
      );
    },

    expand_main_section_by_id(main_section_id) {
      const target_main_section_id = Number(main_section_id);

      if (!target_main_section_id) {
        return;
      }

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) =>
        Number(main_section?.main_section_id ?? main_section?.backend_id ?? main_section?.id ?? null) ===
        target_main_section_id
          ? {
              ...main_section,
              sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) => ({
                ...section,
                expanded: true,
              })),
            }
          : main_section
      );
    },

    toggle_section(section) {
      const target_section_id = Number(
        section?.section_id ??
        section?.backend_id ??
        section?.id ??
        null
      );

      if (!target_section_id) {
        return;
      }

      const current_expanded_state = Boolean(section?.expanded);
      const next_expanded_state = !current_expanded_state;

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => ({
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((item) =>
          Number(item?.section_id ?? item?.backend_id ?? item?.id ?? null) === target_section_id
            ? {
                ...item,
                expanded: next_expanded_state,
              }
            : item
        ),
      }));

      this.sections = (Array.isArray(this.sections) ? this.sections : []).map((item) =>
        Number(item?.section_id ?? item?.backend_id ?? item?.id ?? null) === target_section_id
          ? {
              ...item,
              expanded: next_expanded_state,
            }
          : item
      );
    },

    open_question_modal(section_ref) {
      const section =
        section_ref && typeof section_ref === 'object'
          ? section_ref
          : this.sections[Number(section_ref)] || null;
      this.active_question_section = section;
      this.active_question_section_index =
        section && typeof section_ref === 'number' ? section_ref : this.sections.indexOf(section);
      this.question_text_error = false;
      this.question_forms = [
        this.build_question_form(((this.active_question_section?.questions ?? []).length || 0) + 1),
      ];
      this.question_modal_open = true;
    },

    async open_assign_existing_question_modal(section_ref) {
      const section =
        section_ref && typeof section_ref === 'object'
          ? section_ref
          : this.sections[Number(section_ref)] || null;
      const section_id = Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null);
      const form_id = Number(this.topBar.formId);

      if (!section || !section_id || !form_id) {
        this.show_error('Please select a valid section first.');
        return;
      }

      this.active_assign_section = section;
      this.assign_existing_question_modal_open = true;
      this.is_loading_questions = true;
      this.question_search = '';

      const assigned_question_ids = new Set(
        (Array.isArray(section?.questions) ? section.questions : []).map((question) =>
          Number(question?.question_id ?? question?.backend_id ?? question?.id ?? null)
        )
      );

      const loader = await this.show_loader('Loading existing questions...');

      try {
        const rows = await fetch_questions_in_project({ form_id });
        this.question_list = rows.map((question, index) => {
          const question_id = Number(question?.id ?? question?.question_id ?? index + 1);
          const is_dependent = this.is_dependent_question(question);
          const question_type_label = this.format_question_type_label(
            question?.question_type?.question_type ??
            question?.question_type?.type ??
            question?.question_type ??
            ''
          );

            return {
              id: question_id,
              question_id,
              question_text: String(question?.question_text || question?.question || 'No question text').trim(),
              question_type_label,
              question_number: String(question?.question_number ?? question_id ?? ''),
              is_unique: Boolean(question?.is_unique),
              is_assigned: assigned_question_ids.has(question_id),
              is_dependent,
              assignment_label: is_dependent ? 'Dependent' : 'Available',
              is_saving: false,
          };
        });
      } catch (error) {
        this.question_list = [];
        this.show_error(extract_error_message(error, 'Failed to load questions.'));
      } finally {
        await this.close_loader(loader);
        this.is_loading_questions = false;
      }
    },

    close_assign_existing_question_modal() {
      this.assign_existing_question_modal_open = false;
      this.active_assign_section = null;
      this.question_search = '';
      this.question_list = [];
    },

    async toggle_existing_question_assignment(question) {
      const normalized_question_id = Number(question?.question_id ?? question?.id ?? null);
      const form_id = Number(this.topBar.formId);
      const section_id = Number(
        this.active_assign_section?.section_id ??
        this.active_assign_section?.backend_id ??
        this.active_assign_section?.id ??
        null
      );

      if (!normalized_question_id || !form_id || !section_id) {
        return;
      }

      if (question.is_dependent) {
        this.show_error('Dependent questions cannot be assigned directly.');
        return;
      }

      if (question.is_saving) {
        return;
      }

        question.is_saving = true;

        try {
          if (question.is_assigned) {
            const was_unassigned = await this.unassign_question(this.active_assign_section, question);

            if (was_unassigned) {
              question.is_assigned = false;
            }
            return;
          }

        const response = await axios.post(
          ASSIGN_QUESTIONS_TO_SECTION_URL,
          {
            question_ids: [normalized_question_id],
            form_id,
            section_id,
          },
          get_request_config()
        );
        const parsed_response = this.parse_response_data(response);

        if (parsed_response?.status === 'success') {
          question.is_assigned = true;
          await this.load_form_sections();
          this.expand_section_by_id(section_id);
          this.show_success(parsed_response.message || 'Questions assigned to section successfully.');
          return;
        }

        this.show_error(
          this.normalize_error_message(
            parsed_response?.message,
            'Failed to assign questions to section.'
          )
        );
      } catch (error) {
        const message = this.normalize_error_message(
          extract_error_message(
            error,
            question.is_assigned
              ? 'Failed to unassign question from section.'
              : 'Failed to assign questions to section.'
          ),
          question.is_assigned
            ? 'Failed to unassign question from section.'
            : 'Failed to assign questions to section.'
        );
        this.show_error(message);
      } finally {
        question.is_saving = false;
      }
    },

    async open_edit_question_modal(section_ref, question_ref) {
      const section =
        section_ref && typeof section_ref === 'object'
          ? section_ref
          : this.sections[Number(section_ref)];
      const question =
        question_ref && typeof question_ref === 'object'
          ? question_ref
          : section?.questions?.[Number(question_ref)];
      const question_id = Number(
        question?.question_id ?? question?.id ?? question?.backend_id ?? null
      );

      if (!section || !question_id) {
        this.show_error('Please select a valid question first.');
        return;
      }

      this.edit_question_error = false;
      this.active_edit_section_index = this.sections.indexOf(section);
      this.active_question_index = section?.questions?.indexOf(question) ?? null;
      this.current_edit_question = this.build_edit_question_form(
        question,
        this.active_question_index ?? 0
      );
      this.editing_question = true;

      const loader = await this.show_loader('Loading question...');

      try {
        const parsed_response = await fetch_question_for_edit(question_id);
        const question_data = parsed_response?.data || {};
        const normalized_question = Object.keys(question_data).length
          ? this.build_edit_question_form(question_data, this.active_question_index ?? 0)
          : null;

        if (normalized_question) {
          this.current_edit_question = {
            ...this.current_edit_question,
            ...normalized_question,
            question_id,
            question_text: question_data.question_text ?? normalized_question.question_text,
            question_description:
              question_data.question_description ?? normalized_question.question_description,
            question_type_id:
              question_data?.question_type?.id ??
              question_data?.question_type_id ??
              normalized_question.question_type_id,
            is_mandatory:
              typeof question_data?.is_mandatory === 'boolean'
                ? question_data.is_mandatory
                : normalized_question.is_mandatory,
            question_number:
              question_data?.question_number ?? normalized_question.question_number ?? '',
            dependant_questions: Array.isArray(question_data?.dependant_questions)
              ? question_data.dependant_questions
              : normalized_question.dependant_questions,
          };

          if (
            this.current_edit_question.show_option_inputs &&
            this.current_edit_question.option_inputs.length < 2
          ) {
            this.current_edit_question.option_inputs = ['', ''];
          }
        }

        this.handle_question_type_change_for_edit(this.current_edit_question);

        this.edit_question_modal_open = true;
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to load question details.'));
      } finally {
        await this.close_loader(loader);
        this.editing_question = false;
      }
    },

    async fetch_project_id_for_question(question_id, form_id) {
      const normalized_question_id = Number(question_id);
      const normalized_form_id = Number(form_id);

      if (!normalized_question_id || !normalized_form_id) {
        return '';
      }

      try {
        const rows = await fetch_questions_in_project({ form_id: normalized_form_id });
        const matched_question = (Array.isArray(rows) ? rows : []).find((item) =>
          Number(item?.question_id ?? item?.id ?? null) === normalized_question_id
        );

        if (!matched_question) {
          return '';
        }

        const project_id = matched_question?.project_id ??
          matched_question?.project?.id ??
          matched_question?.question?.project_id ??
          matched_question?.question?.project?.id ??
          '';

        return String(project_id || '').trim();
      } catch {
        return '';
      }
    },

    question_matches_assignment(question = {}, question_id, section_question_id = null) {
      const normalized_question_id = Number(question_id);
      const normalized_section_question_id = Number(section_question_id);
      const candidate_question_id = Number(
        question?.question_id ?? question?.backend_id ?? question?.id ?? null
      );
      const candidate_section_question_id = Number(
        question?.section_question_id ??
        question?.section_question?.id ??
        question?.form_section_question_id ??
        question?.section_question_mapping_id ??
        null
      );

      if (normalized_section_question_id && candidate_section_question_id) {
        return candidate_section_question_id === normalized_section_question_id;
      }

      return Boolean(normalized_question_id && candidate_question_id === normalized_question_id);
    },

    find_section_question(section_id, question_id, section_question_id = null) {
      const normalized_section_id = Number(section_id);

      if (!normalized_section_id) {
        return null;
      }

      const section = (Array.isArray(this.sections) ? this.sections : []).find((item) =>
        Number(item?.section_id ?? item?.backend_id ?? item?.id ?? null) === normalized_section_id
      );

      return (Array.isArray(section?.questions) ? section.questions : []).find((question) =>
        this.question_matches_assignment(question, question_id, section_question_id)
      ) || null;
    },

    async unassign_question(section_ref, question_ref) {
      const section =
        section_ref && typeof section_ref === 'object'
          ? section_ref
          : this.sections[Number(section_ref)];
      const question =
        question_ref && typeof question_ref === 'object'
          ? question_ref
          : section?.questions?.[Number(question_ref)];

      const question_id = Number(
        question?.question_id ?? question?.backend_id ?? question?.id ?? null
      );
      const section_question_id = Number(
        question?.section_question_id ??
        question?.section_question?.id ??
        question?.form_section_question_id ??
        question?.section_question_mapping_id ??
        null
      );
      const section_id = Number(
        section?.section_id ?? section?.backend_id ?? section?.id ?? null
      );
      const form_id = String(this.topBar.formId || this.$route?.query?.form_id || '').trim();

      if (!question_id || !form_id) {
        return false;
      }

      if (section_id) {
        this.set_question_unassigning(section_id, question_id, true);
      }
      const loader = await this.show_loader('Unassigning question...');

      try {
        const response = await unassign_question_from_section({
          question_id,
          question_ids: [question_id],
          section_question_id: section_question_id || undefined,
          section_question_ids: section_question_id ? [section_question_id] : undefined,
          section_id: section_id || undefined,
          form_id,
        });

        if (String(response?.status || '').toLowerCase() !== 'success') {
          throw new Error(response?.message || 'Failed to unassign question.');
        }

        await this.load_form_sections();
        if (section_id) {
          this.expand_section_by_id(section_id);
        }

        if (this.find_section_question(section_id, question_id, section_question_id)) {
          throw new Error('Question was not unassigned. Please try again.');
        }

        this.show_success(response.message || 'Question unassigned successfully');
        return true;
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to unassign question.'));
        if (section_id) {
          this.set_question_unassigning(section_id, question_id, false);
        }
        return false;
      } finally {
        await this.close_loader(loader);
      }
    },

    handle_question_type_change_for_edit(form) {
      const normalized_type = this.get_selected_question_raw_type(form.question_type_id);
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      form.show_option_inputs = requires_options;
      form.multi_select_options = requires_options
        ? (Array.isArray(form.option_inputs) ? form.option_inputs : [])
            .map((option) => String(option || '').trim())
            .filter(Boolean)
        : [];

      if (requires_options && (!Array.isArray(form.option_inputs) || !form.option_inputs.length)) {
        form.option_inputs = ['', ''];
      }
    },

    close_question_modal() {
      this.question_modal_open = false;
      this.question_text_error = false;
      this.active_question_section = null;
      this.active_question_section_index = null;
      this.question_forms = [];
    },

    close_edit_section_modal() {
      this.edit_section_name_error = false;
      this.current_edit_section_index = null;
      this.current_edit_section = {
        section_id: null,
        name: '',
        is_deleting: false,
      };
    },

    set_sub_section_deleting(section_id, is_deleting) {
      const normalized_section_id = Number(section_id);

      if (!normalized_section_id) {
        return;
      }

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) => ({
        ...main_section,
        sections: (Array.isArray(main_section.sections) ? main_section.sections : []).map((section) =>
          Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === normalized_section_id
            ? {
              ...section,
              is_deleting,
            }
            : section
        ),
      }));

      this.sections = (Array.isArray(this.sections) ? this.sections : []).map((section) =>
        Number(section?.section_id ?? section?.backend_id ?? section?.id ?? null) === normalized_section_id
          ? {
            ...section,
            is_deleting,
          }
          : section
      );

      if (Number(this.current_edit_section?.section_id || 0) === normalized_section_id) {
        this.current_edit_section = {
          ...this.current_edit_section,
          is_deleting,
        };
      }
    },

    close_edit_main_section_modal() {
      this.edit_main_section_name_error = false;
      this.current_edit_main_section = {
        main_section_id: null,
        main_section_name: '',
        main_section_description: '',
        is_deleting: false,
      };
    },

    set_main_section_deleting(main_section_id, is_deleting) {
      const normalized_main_section_id = Number(main_section_id);

      if (!normalized_main_section_id) {
        return;
      }

      this.main_sections = (Array.isArray(this.main_sections) ? this.main_sections : []).map((main_section) =>
        Number(main_section?.main_section_id ?? main_section?.backend_id ?? main_section?.id ?? null) === normalized_main_section_id
          ? {
            ...main_section,
            is_deleting,
          }
          : main_section
      );

      if (Number(this.current_edit_main_section?.main_section_id || 0) === normalized_main_section_id) {
        this.current_edit_main_section = {
          ...this.current_edit_main_section,
          is_deleting,
        };
      }
    },

    close_sub_section_modal() {
      this.subsection_modal_open = false;
      this.sub_section_name_error = false;
      this.active_main_section_name = '';
      this.active_main_section_id = null;
      this.new_sub_section = {
        section_name: '',
        order_number: 0,
      };
    },

    close_edit_question_modal() {
      this.edit_question_modal_open = false;
      this.edit_question_error = false;
      this.active_edit_section_index = null;
      this.active_question_index = null;
      this.current_edit_question = {
        question_id: null,
        question_text: '',
        question_description: '',
        question_type_id: '',
        is_mandatory: true,
        question_number: '',
        show_option_inputs: false,
        option_inputs: ['', ''],
        multi_select_options: [],
        dependant_questions: [],
      };
    },

    add_question_form() {
      const next_number = this.question_forms.length + 1;
      this.question_forms.push(this.build_question_form(next_number));
    },

    remove_question_form(index) {
      if (this.question_forms.length <= 1) {
        return;
      }

      this.question_forms.splice(index, 1);
    },

    handle_question_type_change_for_form(form) {
      const selected_type = this.question_types.find(
        (type) => String(type.id) === String(form.question_type_id)
      );
      const normalized_type = String(selected_type?.question_type || '').toLowerCase();
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      form.show_option_inputs = requires_options;
      form.multi_select_options = requires_options
        ? form.option_inputs.map((option) => String(option || '').trim()).filter(Boolean)
        : [];

      if (requires_options && (!Array.isArray(form.option_inputs) || !form.option_inputs.length)) {
        form.option_inputs = ['', ''];
      }

      if (!requires_options) {
        form.dependant_questions = [];
      } else if (!Array.isArray(form.dependant_questions)) {
        form.dependant_questions = [];
      }
    },

    add_option_input(form) {
      if (!Array.isArray(form.option_inputs)) {
        form.option_inputs = [];
      }

      form.option_inputs.push('');
    },

    remove_option_input(form, option_index) {
      if (!Array.isArray(form.option_inputs) || form.option_inputs.length <= 2) {
        return;
      }

      form.option_inputs.splice(option_index, 1);
    },

    get_trigger_options(form) {
      if (!form?.show_option_inputs) {
        return [];
      }

      return (Array.isArray(form.option_inputs) ? form.option_inputs : [])
        .map((option) => String(option || '').trim())
        .filter(Boolean);
    },

    serialize_dependent_questions(dependant_questions) {
      if (!Array.isArray(dependant_questions)) {
        return [];
      }

      return dependant_questions
        .map((dep_question) => {
          const normalized_type = this.get_selected_question_raw_type(dep_question.question_type_id);
          const requires_options =
            normalized_type.includes('single_select') || normalized_type.includes('multi_select');

          return {
            question_text: String(dep_question.question_text || '').trim(),
            question_type_id: Number(dep_question.question_type_id),
            is_mandatory: Boolean(dep_question.is_mandatory),
            is_unique: Boolean(dep_question.is_unique),
            question_number: Number.parseInt(dep_question.question_number, 10) || null,
            options: requires_options && Array.isArray(dep_question.options)
              ? dep_question.options
                  .map((option) => String(option?.choice || option || '').trim())
                  .filter(Boolean)
              : [],
            trigger_condition: String(dep_question.trigger_condition || '').trim() || null,
          };
        })
        .filter((dep) => dep.question_text && dep.question_type_id && dep.trigger_condition);
    },

    get_selected_question_raw_type(question_type_id) {
      const selected_type = this.question_types.find(
        (type) => String(type.id) === String(question_type_id)
      );

      return String(
        selected_type?.question_type ||
        selected_type?.type ||
        selected_type?.formatted_type ||
        ''
      ).toLowerCase();
    },

    build_option_inputs_from_values(values = []) {
      const options = Array.isArray(values)
        ? values
            .map((option) => {
              if (option && typeof option === 'object') {
                return String(
                  option.choice ??
                  option.value ??
                  option.option ??
                  option.name ??
                  option.label ??
                  option.text ??
                  ''
                ).trim();
              }

              return String(option || '').trim();
            })
            .filter(Boolean)
        : [];

      return options.length >= 2 ? options : ['', ''];
    },

    extract_question_option_values(question = {}) {
      return question?.options ||
        question?.multi_select_options ||
        question?.multi_select_choices ||
        question?.choices ||
        question?.question_options ||
        question?.option_values ||
        question?.question_choices ||
        [];
    },

    build_edit_question_form(question = {}, index = 0) {
      const question_id =
        question?.question_id ??
        question?.id ??
        question?.backend_id ??
        null;
      const question_type_id =
        question?.question_type_id ??
        question?.question_type?.id ??
        question?.type ??
        '';
      const option_inputs = this.build_option_inputs_from_values(
        this.extract_question_option_values(question)
      );
      const normalized_type = this.get_selected_question_raw_type(question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      return {
        id: question_id ?? `${Date.now()}-${index}`,
        question_id,
        question_text:
          question?.question_text ??
          question?.question ??
          question?.text ??
          question?.name ??
          '',
        question_description:
          question?.question_description ??
          question?.description ??
          '',
        question_type_id,
        is_mandatory:
          typeof question?.is_mandatory === 'boolean' ? question.is_mandatory : true,
        is_unique: Boolean(question?.is_unique),
        question_number: question?.question_number ?? '',
        show_option_inputs,
        option_inputs,
        multi_select_options: Array.isArray(question?.multi_select_options)
          ? question.multi_select_options
          : option_inputs.filter(Boolean),
        dependant_questions: Array.isArray(question?.dependant_questions)
          ? question.dependant_questions
          : Array.isArray(question?.dependent_questions)
            ? question.dependent_questions
            : [],
      };
    },

    build_edit_question_payload() {
      const selected_type = this.question_types.find(
        (type) => String(type.id) === String(this.current_edit_question.question_type_id)
      );
      const normalized_type = String(selected_type?.question_type || '').toLowerCase();
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');
      const option_values = requires_options
        ? (Array.isArray(this.current_edit_question.option_inputs)
            ? this.current_edit_question.option_inputs
            : [])
            .map((option) => String(option || '').trim())
            .filter(Boolean)
        : [];

      return {
        question_id: this.current_edit_question.question_id,
        question_text: String(this.current_edit_question.question_text || '').trim(),
        question_description: String(this.current_edit_question.question_description || '').trim() || null,
        question_type_id: Number(this.current_edit_question.question_type_id),
        is_mandatory: Boolean(this.current_edit_question.is_mandatory),
        is_unique: Boolean(this.current_edit_question.is_unique),
        question_number: Number.isInteger(this.current_edit_question.question_number)
          ? this.current_edit_question.question_number
          : Number.parseInt(this.current_edit_question.question_number, 10) || null,
        multi_select_options: option_values,
        dependant_questions: [],
        trigger_condition: null,
        section_id: null,
      };
    },

    build_question_payload(form, section_id) {
      const selected_type = this.question_types.find(
        (type) => String(type.id) === String(form.question_type_id)
      );
      const normalized_type = String(selected_type?.question_type || '').toLowerCase();
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');
      const option_values = requires_options
        ? (Array.isArray(form.option_inputs) ? form.option_inputs : [])
            .map((option) => String(option || '').trim())
            .filter(Boolean)
        : [];

      return {
        question_text: String(form.question_text || '').trim(),
        question_description: String(form.question_description || '').trim() || null,
        question_type_id: Number(form.question_type_id),
        is_mandatory: Boolean(form.is_mandatory),
        is_unique: Boolean(form.is_unique),
        question_number: Number.isInteger(form.question_number) ? form.question_number : null,
        multi_select_options: option_values,
        section_id: Number(section_id) || null,
        dependant_questions: [],
        trigger_condition: null,
      };
    },

    async create_main_section() {
      this.main_section_name_error = !this.resolve_main_section_name();
      this.order_number_error =
        !Number.isInteger(this.new_main_section.order_number) ||
        this.new_main_section.order_number < 1;

      if (this.main_section_name_error || this.order_number_error) {
        return;
      }

      this.creating_main_section = true;
      const loader = await this.show_loader('Creating main section...');

      try {
        const formId = Number(this.topBar.formId);

        if (!formId) {
          this.show_error('Please select a valid form first.');
          return;
        }

        const payload = {
          main_section_name: this.resolve_main_section_name(),
          order_number: this.new_main_section.order_number,
          form_id: formId,
        };

        const parsed_response = await create_main_section(payload);

        if (parsed_response?.status === 'success') {
          await this.load_reusable_section_catalogs();
          this.show_success(parsed_response.message || 'Main section created successfully.');
          this.close_modal("create_main_section_modal");
          await this.load_main_sections();
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to create main section.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to create main section.'));
      } finally {
        await this.close_loader(loader);
        this.creating_main_section = false;
      }
    },

    async create_sub_section() {
      this.sub_section_name_error = !this.resolve_sub_section_name();
      this.order_number_error =
        !Number.isInteger(this.new_sub_section.order_number) ||
        this.new_sub_section.order_number < 1;

      if (this.sub_section_name_error || this.order_number_error) {
        return;
      }

      const formId = Number(this.topBar.formId);
      const form_main_section_id = Number(this.active_main_section_id) || null;

      if (!formId) {
        this.show_error('Please select a valid form first.');
        return;
      }

      this.creating_main_section = true;
      const loader = await this.show_loader('Creating sub section...');

      try {
        const payload = {
          form_id: formId,
          section_name: this.resolve_sub_section_name(),
          order_number: this.new_sub_section.order_number,
        };

        if (form_main_section_id) {
          payload.form_main_section_id = form_main_section_id;
        }

        const parsed_response = await create_sub_section_request(payload);

        if (parsed_response?.status === 'success') {
          await this.load_reusable_section_catalogs();
          await this.load_main_sections();
          this.show_success(parsed_response.message || 'Sub section created successfully.');
          this.close_modal('create_sub_section_modal');
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to create sub section.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to create sub section.'));
      } finally {
        await this.close_loader(loader);
        this.creating_main_section = false;
      }
    },

    async save_edited_section() {
      this.edit_section_name_error = !String(this.current_edit_section.name || '').trim();

      if (this.edit_section_name_error) {
        return;
      }

      const section_id = Number(this.current_edit_section.section_id);

      if (!section_id) {
        this.show_error('Please select a valid main section first.');
        return;
      }

      this.editing_main_section = true;
      const loader = await this.show_loader('Updating sub section...');

      try {
        const payload = {
          name: String(this.current_edit_section.name || '').trim(),
          section_id,
        };

        const response = await axios.put(
          UPDATE_FORM_SECTION_URL,
          payload,
          get_request_config()
        );
        const parsed_response = this.parse_response_data(response);

        if (parsed_response?.status === 'success') {
          this.show_success(parsed_response.message || 'Sub section updated successfully.');
          this.close_modal('edit_sub_section_modal');
          await this.load_form_sections();

          const refreshed_section = this.sections.find(
            (item) => Number(item.backend_id ?? item.id) === Number(section_id)
          );

          if (refreshed_section) {
            refreshed_section.expanded = true;
          }

          return;
        }

        this.show_error(parsed_response?.message || 'Failed to update sub section.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to update sub section.'));
      } finally {
        await this.close_loader(loader);
        this.editing_main_section = false;
      }
    },

    async save_edited_main_section() {
      this.edit_main_section_name_error = !String(
        this.current_edit_main_section.main_section_name || ''
      ).trim();

      if (this.edit_main_section_name_error) {
        return;
      }

      const main_section_id = Number(this.current_edit_main_section.main_section_id);

      if (!main_section_id) {
        this.show_error('Please select a valid main section first.');
        return;
      }

      this.editing_main_section = true;
      const loader = await this.show_loader('Updating main section...');

      try {
        const payload = {
          main_section_id,
          main_section_name: String(
            this.current_edit_main_section.main_section_name || ''
          ).trim(),
          main_section_description:
            String(this.current_edit_main_section.main_section_description || '').trim() || null,
        };

        const parsed_response = await update_main_section(payload);

        if (parsed_response?.status === 'success') {
          this.show_success(parsed_response.message || 'Main section updated successfully.');
          this.close_modal('edit_main_section_modal');
          await this.load_main_sections();
          this.expand_main_section_by_id(main_section_id);
          return;
        }

        this.show_error(parsed_response?.message || 'Failed to update main section.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to update main section.'));
      } finally {
        await this.close_loader(loader);
        this.editing_main_section = false;
      }
    },

    delete_sub_section(section = {}) {
      const section_id = Number(
        section?.section_id ??
        section?.backend_id ??
        section?.id ??
        this.current_edit_section?.section_id
      );
      const form_id = Number(this.topBar.formId);
      const main_section_id = Number(section?.main_section_id ?? null);
      const section_name = String(
        section?.name ??
        section?.section_name ??
        this.current_edit_section?.name ??
        'this sub section'
      ).trim();

      if (!section_id || !form_id) {
        this.show_error('Please select a valid sub section first.');
        return;
      }

      this.show_confirm(
        'Delete Sub Section',
        `Are you sure you want to delete ${section_name}?`,
        async () => {
          this.deleting_sub_section = true;
          this.set_sub_section_deleting(section_id, true);
          const loader = await this.show_loader('Deleting sub section...');

          try {
            const parsed_response = await soft_delete_form_section({
              section_id: String(section_id),
              form_id: String(form_id),
            });

            if (String(parsed_response?.status || '').toLowerCase() !== 'success') {
              throw new Error(parsed_response?.message || 'Failed to delete sub section.');
            }

            this.close_modal('edit_sub_section_modal');
            this.close_edit_section_modal();
            await this.load_form_sections();
            if (main_section_id) {
              this.expand_main_section_by_id(main_section_id);
            }
            this.show_success(parsed_response.message || 'Section successfully deleted');
          } catch (error) {
            this.show_error(extract_error_message(error, 'Failed to delete sub section.'));
          } finally {
            await this.close_loader(loader);
            this.deleting_sub_section = false;
            this.set_sub_section_deleting(section_id, false);
          }
        }
      );
    },

    delete_main_section(main_section = {}) {
      const main_section_id = Number(
        main_section?.main_section_id ??
        main_section?.id ??
        this.current_edit_main_section.main_section_id
      );
      const form_id = Number(this.topBar.formId);
      const main_section_name = String(
        main_section?.main_section_name ??
        main_section?.name ??
        this.current_edit_main_section.main_section_name ??
        'this main section'
      ).trim();

      if (!main_section_id || !form_id) {
        this.show_error('Please select a valid main section first.');
        return;
      }

      this.show_confirm(
        'Delete Main Section',
        `Are you sure you want to delete ${main_section_name}?`,
        async () => {
          this.deleting_main_section = true;
          this.set_main_section_deleting(main_section_id, true);
          const loader = await this.show_loader('Deleting main section...');

          try {
            const parsed_response = await delete_main_section({
              main_section_id,
              form_id,
            });

            if (String(parsed_response?.status || '').toLowerCase() !== 'success') {
              this.show_error(parsed_response?.message || 'Failed to delete main section.');
            }

            this.close_modal('edit_main_section_modal');
            this.close_edit_main_section_modal();
            await this.load_main_sections();
            this.show_success(parsed_response.message || 'Main section deleted successfully.');
          } catch (error) {
            this.show_error(extract_error_message(error, 'Failed to delete main section.'));
          } finally {
            await this.close_loader(loader);
            this.deleting_main_section = false;
            this.set_main_section_deleting(main_section_id, false);
          }
        }
      );
    },

    async save_all_questions() {
      this.question_text_error = this.question_forms.some(
        (form) => !String(form.question_text || '').trim() || !String(form.question_type_id || '').trim()
      );

      if (this.question_text_error) {
        return;
      }

      const invalid_dependent = this.question_forms.some((form) => {
        if (!form?.show_option_inputs || !Array.isArray(form.dependant_questions) || !form.dependant_questions.length) {
          return false;
        }

        return form.dependant_questions.some((dep) => {
          const dep_text = String(dep?.question_text || '').trim();
          const dep_type_id = String(dep?.question_type_id || '').trim();
          const trigger = String(dep?.trigger_condition || '').trim();
          return !dep_text || !dep_type_id || !trigger;
        });
      });

      if (invalid_dependent) {
        this.show_error('Please complete all required fields for dependent questions (text, type, and trigger condition).');
        return;
      }

      const formId = Number(this.topBar.formId);
      const active_section = this.active_question_section;
      const sectionId = Number(
        active_section?.id ??
        active_section?.section_id ??
        null
      );

      if (!active_section || !formId || !sectionId) {
        console.log(active_section, formId, sectionId)
        this.show_error('Please select a valid section first.');
        return;
      }

      this.creating_question = true;
      const loader = await this.show_loader('Saving questions...');

      try {

        const created_question_ids = [];
        const local_questions = [];

        for (const form of this.question_forms) {
          const payload = this.build_question_payload(form, sectionId);
          const parsed_create_response = await create_question(payload);
          const question_id = parsed_create_response?.data?.question_id;

          if (!question_id) {
            throw new Error(parsed_create_response?.message || 'Failed to create question.');
          }

          if (form?.show_option_inputs && Array.isArray(form.dependant_questions) && form.dependant_questions.length) {
            const dependent_questions = this.serialize_dependent_questions(form.dependant_questions);

            for (const dep of dependent_questions) {
              const assign_payload = {
                main_question_id: Number(question_id),
                dependent_data: {
                  question_text: dep.question_text,
                  question_type_id: dep.question_type_id,
                  is_mandatory: dep.is_mandatory,
                  is_unique: dep.is_unique,
                  trigger_condition: dep.trigger_condition,
                  multi_select_options: dep.options,
                },
              };

              const assign_response = await assign_dependent_question(assign_payload);

              if (assign_response?.status !== 'success') {
                throw new Error(assign_response?.message || 'Failed to assign a dependent question.');
              }
            }
          }

          created_question_ids.push(question_id);

          const selected_type = this.question_types.find(
            (type) => String(type.id) === String(form.question_type_id)
          );

          local_questions.push({
            id: question_id,
            text: payload.question_text,
            description: payload.question_description || '',
            type_label: selected_type?.formatted_type || '',
          });
        }

        const assign_response = await axios.post(
          ASSIGN_QUESTIONS_TO_SECTION_URL,
          {
            question_ids: created_question_ids,
            form_id: formId,
            section_id: sectionId,
          },
          get_request_config()
        );
        const parsed_assign_response = this.parse_response_data(assign_response);

        if (parsed_assign_response?.status === 'success') {
          this.show_success(
            parsed_assign_response.message || 'Questions assigned to section successfully.'
          );
          this.close_modal("create_question_modal");
          await this.load_form_sections();

          const refreshed_section = this.sections.find(
            (item) => Number(item.backend_id ?? item.id ?? item.section_id) === Number(sectionId)
          );

          if (refreshed_section) {
            refreshed_section.expanded = true;
          }
          return;
        }

        this.show_error(
          this.normalize_error_message(
            parsed_assign_response?.message,
            'Failed to assign questions to section.'
          )
        );
      } catch (error) {
        const message = this.normalize_error_message(
          extract_error_message(error, 'Failed to save questions.'),
          'Failed to save questions.'
        );
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.creating_question = false;
      }
    },

    async save_edited_question() {
      const question_text = String(this.current_edit_question.question_text || '').trim();
      const question_type_id = String(this.current_edit_question.question_type_id || '').trim();

      if (!this.current_edit_question.question_id) {
        this.show_error('Please select a valid question first.');
        return;
      }

      if (!question_text) {
        this.show_error('Please enter a question text.');
        return;
      }

      if (!question_type_id) {
        this.show_error('Please select a question type.');
        return;
      }

      this.edit_question_error = false;
      this.editing_question = true;
      const loader = await this.show_loader('Saving question...');

      try {
        const response = await update_question(this.build_edit_question_payload());

        if (response?.status !== 'success') {
          this.show_error(response?.message || 'Failed to update question.');
          return;
        }

        await this.load_form_sections();
        const section = this.sections[this.active_edit_section_index];
        if (section) {
          section.expanded = true;
        }
        this.close_modal('edit_question_modal');
        this.show_success(response.message || 'Question updated successfully.');
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to update question.'));
      } finally {
        await this.close_loader(loader);
        this.editing_question = false;
      }
    },
  },
};
